import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadParquet } from '@/api/endpoints';
import { formatBytes, formatCount } from '@/lib/format';
import { asQueryError } from '@/query/execute';
import { ErrorState } from '@/components/common/states';
import type { RegisteredFile } from '@/types/api';

export interface UploadDialogProps {
  readonly onClose: () => void;
  readonly onRegistered: (file: RegisteredFile) => void;
}

/** Registers an uploaded .parquet as a Dataset (spec §4.6). */
export function UploadDialog({ onClose, onRegistered }: UploadDialogProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const queryClient = useQueryClient();

  const upload = useMutation({
    mutationFn: (target: File) => uploadParquet(target),
    onSuccess: (registered) => {
      void queryClient.invalidateQueries({ queryKey: ['catalog'] });
      onRegistered(registered);
    },
  });

  const accept = (candidate: File | undefined): void => {
    if (candidate) setFile(candidate);
  };

  return (
    <div className="overlay" role="presentation" onMouseDown={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="panel-head">
          <span className="eyebrow" id="upload-title">
            Register a Parquet file
          </span>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="p-3">
          <div
            className="flex flex-col items-center justify-center gap-2 rounded-sm border border-dashed p-6 transition-colors"
            style={{
              borderColor: dragging ? 'var(--c-local)' : 'var(--c-line-strong)',
              background: dragging ? 'var(--c-local-dim)' : 'transparent',
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              accept(event.dataTransfer.files[0]);
            }}
          >
            <p className="text-sm text-ink">Drop a .parquet file here</p>
            <p className="text-xs text-muted">
              It lands in your staging prefix and becomes a queryable table.
            </p>
            <button type="button" className="btn" onClick={() => inputRef.current?.click()}>
              Choose file
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".parquet"
              className="sr-only"
              aria-label="Parquet file"
              onChange={(event) => accept(event.target.files?.[0])}
            />
          </div>

          {file ? (
            <div className="mt-3 flex items-center gap-2 rounded-sm border border-line bg-raised px-2.5 py-2">
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink">{file.name}</span>
              <span className="tnum shrink-0 text-[10px] text-faint">{formatBytes(file.size)}</span>
              <button
                type="button"
                className="btn btn-run"
                disabled={upload.isPending}
                onClick={() => upload.mutate(file)}
              >
                {upload.isPending ? 'Registering…' : 'Register'}
              </button>
            </div>
          ) : null}

          {upload.error ? <ErrorState error={asQueryError(upload.error)} /> : null}

          {upload.data ? (
            <div className="mt-3 rounded-sm border border-line bg-raised px-2.5 py-2">
              <p className="text-xs text-ok">
                Registered as{' '}
                <code className="font-mono">
                  {upload.data.schema}.{upload.data.table}
                </code>
              </p>
              <p className="tnum mt-0.5 text-[10px] text-faint">
                {formatCount(upload.data.rowCount)} rows · {upload.data.columns.length} columns ·{' '}
                {formatBytes(upload.data.sizeBytes)}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
